import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout, Spin, App as AntdApp } from 'antd'
import { motion } from 'framer-motion'

import { DashboardLayout } from './Layouts/DashboardLayout'
import { AuthProvider } from './contexts/AuthContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { PopoutWindowProvider } from './components/nexprof/PopoutWindowManager'
import { BrowserWindowProvider } from './components/nexprof/BrowserWindowManager'

// CORRECTED LAZY IMPORTS - THESE ACTUALLY WORK!
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome').then(m => ({ default: m.DashboardHome })))
const LiveCallsPage = lazy(() => import('./pages/dashboard/LiveCallsPage').then(m => ({ default: m.LiveCallsPage })))
const DNADashboard = lazy(() => import('./pages/dashboard/DNADashboard').then(m => ({ default: m.DNADashboard })))

// Personal AI Assistant Pages - FIXED IMPORTS
const AIPersona = lazy(() => import('./pages/aipersona/AIPersona'))
const PersonalAssistant = lazy(() => import('./pages/personal/PersonalAssistant'))
const RelationshipManager = lazy(() => import('./pages/personal/RelationshipManager'))
const ContextSettings = lazy(() => import('./pages/personal/ContextSettings'))

// Collaboration Pages - FIXED IMPORTS
const CollaborationHub = lazy(() => import('./pages/collaboration/CollaborationHub'))

// Projects portfolio (Track 20260612-human-context-arc — ProjectContext control surface).
// Page + nav entry already existed; the /projects route now renders the real workspace
// (enabled live per Dan 2026-06-19; previously a ComingSoon placeholder).
const ProjectsWorkspace = lazy(() => import('./pages/projects/ProjectsWorkspace').then(m => ({ default: m.ProjectsWorkspace })))

// Agent Interface Pages - FIXED IMPORTS
const AgentInterface = lazy(() => import('./pages/agent/AgentInterface'))

// Personal AI Chat - Direct chat with Claude
const PersonalAIChat = lazy(() => import('./pages/personal-ai'))

// NexChat - Revolutionary consciousness-first chat interface (2026-02-04)
const NexChatPage = lazy(() => import('./pages/NexChatPage'))

// Track 20260520-unified-persona-interface — Phase 2.8: ChatConsole at /console.
// Feature-flagged via VITE_CONSOLE_UI_ENABLED env var (default false). NexChat at
// /nex-chat remains operational throughout. Per MetCog ratification 02 Amendment 4,
// Phase 3+ wires events from the conversation-event-stream backend; Phase 1-2 are
// pure frontend and can run in parallel with that track.
const ChatConsolePage = lazy(() => import('./pages/ChatConsolePage'))

// Dev preview — standalone "Shared Sight" image-experience tuning surface (2026-06-23).
// Relocated out of ChatConsole's stale motion playground onto its own throwaway route.
// No auth wrapper — sits with the other standalone routes below.
const SharedSightPreview = lazy(() => import('./pages/SharedSightPreview'))

// Appointment & Calendar Pages
const BusinessScheduler = lazy(() => import('./pages/appointments/BusinessScheduler'))
const PersonalCalendar = lazy(() => import('./pages/appointments/PersonalAssistant'))
const CalendarSettings = lazy(() => import('./pages/appointments/CalendarSettings'))
const CalendarIntegration = lazy(() => import('./pages/appointments/CalendarIntegration'))
const NotificationManager = lazy(() => import('./pages/notifications/NotificationManager'))

// Orchestration & AI Management Pages
const TaskOrchestrationDashboard = lazy(() => import('./pages/orchestration/TaskOrchestrationDashboard'))
const MultiAIConsensusInterface = lazy(() => import('./pages/orchestration/MultiAIConsensusInterface'))
const MemoryLearningVisualization = lazy(() => import('./pages/orchestration/MemoryLearningVisualization'))
const AgentManagementInterface = lazy(() => import('./pages/orchestration/AgentManagementInterface'))
const MultiAICoordination = lazy(() => import('./pages/orchestration/MultiAICoordination'))
const TranscendentOrchestrationDashboard = lazy(() => import('./pages/orchestration/TranscendentOrchestrationDashboard'))
const QuantumExecutionVisualizer = lazy(() => import('./pages/orchestration/QuantumExecutionVisualizer'))

// Admin Pages
const TenantManagement = lazy(() => import('./pages/admin/TenantManagement'))
const AIContextTester = lazy(() => import('./pages/admin/AIContextTester'))
const ModelRegistry = lazy(() => import('./pages/admin/ModelRegistry'))
const ReadingModesAdmin = lazy(() => import('./pages/admin/ReadingModesAdmin'))
const TestRegistry = lazy(() => import('./pages/admin/TestRegistry'))
const PrivacyAdmin = lazy(() => import('./pages/admin/PrivacyAdmin'))
const SystemSettingsAdmin = lazy(() => import('./pages/admin/SystemSettingsAdmin'))
const ConsensusObservatory = lazy(() => import('./pages/admin/ConsensusObservatory'))

// Warden v1.2 — operator console (Phase 2D.1: route scaffolding, 2D.2-2D.5: live data)
const WardenAdminLayout = lazy(() => import('./pages/admin/warden/WardenAdminLayout'))
const BastionOverview = lazy(() => import('./pages/admin/warden/BastionOverview'))
const WatchfireHall = lazy(() => import('./pages/admin/warden/WatchfireHall'))
const AmendmentVault = lazy(() => import('./pages/admin/warden/AmendmentVault'))
const CodexHall = lazy(() => import('./pages/admin/warden/CodexHall'))
const ChainAtrium = lazy(() => import('./pages/admin/warden/ChainAtrium'))
const InvestigationSurface = lazy(() => import('./pages/admin/warden/InvestigationSurface'))
const Armory = lazy(() => import('./pages/admin/warden/Armory'))
const CeremonyForge = lazy(() => import('./pages/admin/warden/CeremonyForge'))

// Automation Pages
const ProcedureManagement = lazy(() => import('./pages/automation/ProcedureManagement'))

// Consciousness Emergence Pages
const ConsciousnessAdminDashboard = lazy(() => import('./pages/ConsciousnessAdminDashboard'))
const ConsciousnessEmergencePage = lazy(() => import('./pages/ConsciousnessEmergencePage'))
const ConsciousnessWebDashboard = lazy(() => import('./pages/ConsciousnessWebDashboard'))

// Testing Pages
const SharingSuggestionsTest = lazy(() => import('./pages/testing/SharingSuggestionsTest'))

// NexComm Admin UI - Unified Communications Management
const NexCommCommandCenter = lazy(() => import('./pages/nexcomm/NexCommCommandCenter').then(m => ({ default: m.NexCommCommandCenter })))
const NexCommChannels = lazy(() => import('./pages/nexcomm/NexCommChannels').then(m => ({ default: m.NexCommChannels })))
const NexCommActivityStream = lazy(() => import('./pages/nexcomm/NexCommActivityStream').then(m => ({ default: m.NexCommActivityStream })))
const NexCommArtifactWorkspace = lazy(() => import('./pages/nexcomm/NexCommArtifactWorkspace').then(m => ({ default: m.NexCommArtifactWorkspace })))
const NexCommNotifications = lazy(() => import('./pages/nexcomm/NexCommNotifications').then(m => ({ default: m.NexCommNotifications })))
const NexCommSettings = lazy(() => import('./pages/nexcomm/NexCommSettings').then(m => ({ default: m.NexCommSettings })))
const OAuthCallback = lazy(() => import('./pages/nexcomm/OAuthCallback').then(m => ({ default: m.OAuthCallback })))
const NexSentinelDashboard = lazy(() => import('./pages/nexcomm/NexSentinelDashboard'))

// NexProf Pages - "RAG = Library. NexProf = Professor."
const NexProfDashboard = lazy(() => import('./pages/nexprof/NexProfDashboard'))
const PersonPage = lazy(() => import('./pages/people/PersonPage'))
const NexProfManagement = lazy(() => import('./pages/nexprof/NexProfManagement'))
const NexProfSettings = lazy(() => import('./pages/nexprof/NexProfSettings'))

// NexCompass Dashboard - Standalone commitment architecture visualization
const NexCompassDashboard = lazy(() => import('./pages/nexprof/NexCompassDashboard'))

// NexEmerge Popup Windows - Standalone pages for browser window.open()
const ReadingTheatrePage = lazy(() => import('./pages/nexprof/ReadingTheatrePage'))
const ConsultWindowPage = lazy(() => import('./pages/nexprof/ConsultWindowPage'))
const CognitiveForgePage = lazy(() => import('./pages/nexprof/CognitiveForgePage'))
const ArtifactDetailPage = lazy(() => import('./pages/nexcomm/ArtifactDetailPage'))

// Auth Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })))

// Loading component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-900">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="text-center"
    >
      <Spin size="large" />
      <div className="mt-4 text-lg text-gray-400">Loading revolutionary interface...</div>
    </motion.div>
  </div>
)

// Placeholder component for missing pages
const ComingSoon: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex items-center justify-center min-h-96">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center p-8 bg-slate-800/50 rounded-lg border border-purple-500/20"
    >
      <h2 className="text-2xl font-semibold text-white mb-4">🚧 {title}</h2>
      <p className="text-gray-400 mb-6">This revolutionary feature is coming soon!</p>
      <div className="text-sm text-gray-500">
        ✨ Part of the world's first AI-mediated collaboration platform
      </div>
    </motion.div>
  </div>
)

// Simple error boundary component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error?: Error}> {
  constructor(props: {children: React.ReactNode}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error boundary caught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-slate-900">
          <div className="text-center p-8 bg-slate-800/80 rounded-lg border border-red-500/20">
            <h1 className="text-2xl font-bold text-white mb-4">🔧 Loading Issue</h1>
            <p className="text-gray-400 mb-4">The revolutionary platform encountered a loading issue.</p>
            <button 
              onClick={() => {
                this.setState({ hasError: false })
                window.location.reload()
              }} 
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Reload Dashboard
            </button>
            <div className="mt-4 text-sm text-gray-500">
              Error: {this.state.error?.message}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const App: React.FC = () => {
  // Phase C.1.9 (2026-04-30) — one-shot app-load cleanup of stale localStorage keys
  // left over from PersonalAIChat's pre-migration NexProf-selector flow. The
  // PersonalAIChat dropdown is retiring (NexChat is canonical), and stale
  // `selectedNexProfId` left in localStorage would re-bind PersonalAIChat to a
  // ghost persona on next visit. Clear once at boot, log if it was present so
  // we have telemetry on how many users carried the zombie key forward.
  useEffect(() => {
    try {
      const stale = localStorage.getItem('selectedNexProfId');
      if (stale) {
        console.info('[migration C.1.9] cleared stale selectedNexProfId from localStorage', { value: stale });
        localStorage.removeItem('selectedNexProfId');
      }
    } catch (err) {
      // localStorage can throw in private browsing / quota contexts; non-fatal.
      console.warn('[migration C.1.9] localStorage cleanup skipped:', err);
    }
  }, []);

  return (
    <AntdApp>
      <ErrorBoundary>
        <AuthProvider>
          <WebSocketProvider>
            <NotificationProvider>
              <BrowserWindowProvider>
                <PopoutWindowProvider>
                  <Layout className="min-h-screen bg-slate-900">
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                  {/* Auth Routes */}
                  <Route path="/auth/login" element={<LoginPage />} />
                  <Route path="/auth/register" element={<RegisterPage />} />

                  {/* NexEmerge Popup Windows - Standalone routes for window.open() */}
                  <Route path="/nexprof/reading-theatre" element={<ReadingTheatrePage />} />
                  <Route path="/nexprof/consult" element={<ConsultWindowPage />} />
                  <Route path="/nexprof/cognitive-forge" element={<CognitiveForgePage />} />
                  <Route path="/nexcomm/artifacts/:artifactId" element={<ArtifactDetailPage />} />

                  {/* NexChat - Standalone fullscreen chat (can be opened in new window) */}
                  <Route path="/nex-chat-standalone" element={<NexChatPage />} />

                  {/* Dev preview — "Shared Sight" image-experience tuning surface.
                      Standalone, no auth (matches the sibling dev/standalone routes). */}
                  <Route path="/dev/shared-sight" element={<SharedSightPreview />} />

                  {/* Track 20260520 — ChatConsole (unified persona interface) at /console.
                      Feature-flagged via VITE_CONSOLE_UI_ENABLED. Phase 2 ships UI shell;
                      Phase 3+ wires the conversation-event-stream backend events. */}
                  {import.meta.env.VITE_CONSOLE_UI_ENABLED === 'true' && (
                    <Route path="/console" element={<ChatConsolePage />} />
                  )}

                  {/* Dashboard Routes */}
                  <Route path="/" element={<DashboardLayout />}>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardHome />} />
                    <Route path="dashboard/calls" element={<LiveCallsPage />} />
                    <Route path="dashboard/dna" element={<DNADashboard />} />
                    
                    {/* WORKING PAGES - THESE ARE REAL! */}
                    <Route path="ai-persona" element={<AIPersona />} />
                    <Route path="personal" element={<PersonalAssistant />} />
                    <Route path="personal/relationships" element={<RelationshipManager />} />
                    <Route path="personal/context" element={<ContextSettings />} />
                    <Route path="collaboration" element={<CollaborationHub />} />
                    <Route path="agent" element={<AgentInterface />} />
                    <Route path="chat" element={<PersonalAIChat />} />
                    <Route path="nex-chat" element={<NexChatPage />} />
                    
                    {/* NEW SCHEDULING PAGES */}
                    <Route path="business-scheduler" element={<BusinessScheduler />} />
                    <Route path="personal-calendar" element={<PersonalCalendar />} />
                    <Route path="calendar-settings" element={<CalendarSettings />} />
                    <Route path="calendar-integration" element={<CalendarIntegration />} />
                    <Route path="notifications" element={<NotificationManager />} />
                    
                    {/* ORCHESTRATION & AI MANAGEMENT PAGES */}
                    <Route path="orchestration" element={<TaskOrchestrationDashboard />} />
                    <Route path="orchestration/consensus" element={<MultiAIConsensusInterface />} />
                    <Route path="orchestration/memory" element={<MemoryLearningVisualization />} />
                    <Route path="orchestration/agents" element={<AgentManagementInterface />} />
                    <Route path="orchestration/coordination" element={<MultiAICoordination />} />
                    <Route path="orchestration/transcendent" element={<TranscendentOrchestrationDashboard />} />
                    <Route path="orchestration/quantum" element={<QuantumExecutionVisualizer />} />

                    {/* TESTING PAGES */}
                    <Route path="testing/sharing-suggestions" element={<SharingSuggestionsTest />} />

                    {/* NEXPROF - "RAG = Library. NexProf = Professor." */}
                    <Route path="nexprof" element={<NexProfDashboard />} />
                    <Route path="nexprof/management" element={<NexProfManagement />} />
                    <Route path="nexprof/compass" element={<NexCompassDashboard />} />
                    <Route path="nexprof/settings" element={<NexProfSettings />} />
                    <Route path="people/:participantId" element={<PersonPage />} />

                    {/* NEXCOMM - Unified Communications Admin */}
                    <Route path="nexcomm" element={<NexCommCommandCenter />} />
                    <Route path="nexcomm/channels" element={<NexCommChannels />} />
                    <Route path="nexcomm/activity" element={<NexCommActivityStream />} />
                    <Route path="nexcomm/artifacts" element={<NexCommArtifactWorkspace />} />
                    <Route path="nexcomm/notifications" element={<NexCommNotifications />} />
                    <Route path="nexcomm/settings" element={<NexCommSettings />} />
                    <Route path="nexcomm/oauth-callback" element={<OAuthCallback />} />
                    <Route path="nexcomm/sentinel" element={<NexSentinelDashboard />} />

                    {/* Projects workspace — LIVE (W1.4 ProjectContext governance + Memory Core Stratum) */}
                    <Route path="projects" element={<ProjectsWorkspace />} />
                    {/* PLACEHOLDER ROUTES - COMING SOON */}
                    <Route path="projects/team-context" element={<ComingSoon title="Team Context" />} />
                    <Route path="projects/cultural-bridge" element={<ComingSoon title="Cultural Bridge" />} />
                    
                    <Route path="business" element={<ComingSoon title="Business Admin" />} />
                    <Route path="business/settings" element={<ComingSoon title="Tenant Settings" />} />
                    <Route path="business/analytics" element={<ComingSoon title="Business Analytics" />} />
                    <Route path="business/leads" element={<ComingSoon title="Lead Management" />} />
                    <Route path="business/roi" element={<ComingSoon title="ROI Dashboard" />} />
                    
                    <Route path="agent-dashboard" element={<ComingSoon title="Agent Dashboard" />} />
                    <Route path="agent-dashboard/queue" element={<ComingSoon title="Call Queue" />} />
                    <Route path="agent-dashboard/briefing" element={<ComingSoon title="Customer Briefing" />} />
                    <Route path="agent-dashboard/translation" element={<ComingSoon title="Translation Assist" />} />
                    
                    <Route path="customer" element={<ComingSoon title="Customer Portal" />} />
                    <Route path="customer/chat" element={<ComingSoon title="Multi-Modal Chat" />} />
                    <Route path="customer/appointments" element={<ComingSoon title="Appointment Booking" />} />
                    <Route path="customer/history" element={<ComingSoon title="Service History" />} />
                    
                    <Route path="admin" element={<ComingSoon title="System Admin" />} />
                    <Route path="admin/platform" element={<ComingSoon title="Platform Overview" />} />
                    <Route path="admin/tenants" element={<TenantManagement />} />
                    <Route path="admin/permission-test" element={<AIContextTester />} />
                    <Route path="admin/consciousness" element={<ConsciousnessAdminDashboard />} />
                    <Route path="admin/consciousness-web" element={<ConsciousnessWebDashboard />} />
                    <Route path="admin/ai-performance" element={<ComingSoon title="AI Performance" />} />
                    <Route path="admin/scaling" element={<ComingSoon title="Scaling Metrics" />} />
                    <Route path="admin/features" element={<ComingSoon title="Feature Flags" />} />
                    <Route path="admin/automation" element={<ProcedureManagement />} />
                    <Route path="admin/models" element={<ModelRegistry />} />
                    <Route path="admin/reading-modes" element={<ReadingModesAdmin />} />
                    <Route path="admin/observability/tests" element={<TestRegistry />} />
                    <Route path="admin/privacy" element={<PrivacyAdmin />} />
                    <Route path="admin/system-settings" element={<SystemSettingsAdmin />} />
                    <Route path="admin/consensus-observatory" element={<ConsensusObservatory />} />

                    {/* Warden v1.2 — The Bastion (operator console)
                        Phase 2D.1: route scaffolding + Bastion overview + 4 hall shells
                        Phase 2D.2-2D.5: live data wired into each hall */}
                    <Route path="admin/warden" element={<WardenAdminLayout />}>
                      <Route index element={<BastionOverview />} />
                      <Route path="alerts" element={<WatchfireHall />} />
                      <Route path="amendments" element={<AmendmentVault />} />
                      <Route path="reviews" element={<CodexHall />} />
                      <Route path="integrity" element={<ChainAtrium />} />
                      {/* Investigation Surface — track 20260503-warden-investigation-surface (Phase 1).
                          Composes 5 evidence sources into one operator drill-down. URL params:
                          ?personaId={id}&alertId={id?}. Bare URL renders the persona picker. */}
                      <Route path="investigate" element={<InvestigationSurface />} />
                      {/* The Armory — MCP toolkit smoke verification (track 20260506-warden-mcp-toolkit
                          Phase 1 closure). Drives all 14 Warden tools through the registry → provider →
                          handler chain so dispatch bugs surface during smoke, not during real consult. */}
                      <Route path="armory" element={<Armory />} />
                      {/* Ceremony Forge — partition migration orchestration (track
                          20260514-cognitivestate-partition-migration). Lists all 9
                          queued migrations (Phase 3: 3, Phase 5: 6) with phase
                          indicator + start-backfill + G2/G3/G4 audit + rollback
                          recipe per CeremonyPanel. Reusable across all 9. */}
                      <Route path="ceremony" element={<CeremonyForge />} />
                    </Route>

                    {/* CONSCIOUSNESS EMERGENCE - Production Showcase */}
                    <Route path="consciousness" element={<ConsciousnessEmergencePage />} />
                    
                    {/* Catch-all route */}
                    <Route path="*" element={<ComingSoon title="Page Under Construction" />} />
                  </Route>
                  </Routes>
                </Suspense>
                </Layout>
              </PopoutWindowProvider>
            </BrowserWindowProvider>
          </NotificationProvider>
        </WebSocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  </AntdApp>
  )
}

export default App